import apiClient from "./baseService";
import {Company} from "../types/domain/company";
import {handleAxiosError} from "./axiosErrorHandler";
import {sortBy} from "lodash";
import {FarmUnitData, MaderaFieldResult, Parcel} from "../types/domain/parcel";
import {GetMaderaReportOptions, MaderaReportLink, ReportType} from "../types/domain/maderaReports";
import dayjs from "dayjs";
import {FieldHistoryDaysType} from "../types/fieldHistoryDays";

export interface PostCompanyQueryType {
    claimEditorRole?: boolean;
}
export type PostCompanyBodyType = Pick<Company, "properties" | "isReseller" | "isApiEnabled" | "parentCompanyId">;
export type PatchCompanyBodyType = Partial<
    Pick<Company, "properties" | "isReseller" | "isApiEnabled" | "parentCompanyId">
>;

export const getCompanyById = async (companyId: string): Promise<Company> =>
    await apiClient
        .get<Company>(`/companies/${companyId}`)
        .then((response) => response.data)
        .catch(handleAxiosError);

export const getCompanyHierarchy = async (companyId: string): Promise<Company[]> =>
    await apiClient
        .get<Company[]>(`/companies/${companyId}/hierarchy`)
        .then((response) => response.data)
        .catch(handleAxiosError);

export const getCompanies = async (): Promise<Company[]> =>
    await apiClient
        .get<Company[]>(`/companies`)
        .then((response) => response.data)
        .then((data) => {
            return sortBy(data, [(item) => item.properties.name]);
        })
        .catch(handleAxiosError);

export const createCompany = async (body: PostCompanyBodyType, query: PostCompanyQueryType): Promise<Company> =>
    await apiClient
        .post<Company>(`/companies`, body, {params: query})
        .then((response) => response.data)
        .catch(handleAxiosError);

export const patchCompany = async (companyId: string, body: PatchCompanyBodyType): Promise<Company> =>
    await apiClient
        .patch<Company>(`/companies/${companyId}`, body)
        .then((response) => response.data)
        .catch(handleAxiosError);

export const deleteCompany = async (companyId: string) =>
    await apiClient.delete(`/companies/${companyId}`).catch(handleAxiosError);

export const getCompanyMaderaParcelsByFarmId = async (companyId: string, farmId: string): Promise<Parcel[]> =>
    await apiClient
        .get<Parcel[]>(`/companies/${companyId}/madera/farms/${farmId}/parcels`)
        .then((response) => response.data)
        .catch(handleAxiosError);

export const getCompanyMaderaFarmUnits = async (companyId: string): Promise<FarmUnitData[]> =>
    await apiClient
        .get<FarmUnitData[]>(`/companies/${companyId}/madera/farmUnits`)
        .then((response) => response.data)
        .catch(handleAxiosError);

export const getCompanyMaderaFieldResults = async (
    companyId: string,
    fieldId: string,
    date: dayjs.Dayjs,
    range: FieldHistoryDaysType,
): Promise<MaderaFieldResult> => {
    return apiClient
        .get<MaderaFieldResult>(
            `/companies/${companyId}/madera/fields/${fieldId}/results/${date.format("YYYY-MM-DD")}`,
            {
                params: {range},
            },
        )
        .then((response) => response.data)
        .catch(handleAxiosError);
};

export const getCompanyMaderaReport = async (options: GetMaderaReportOptions): Promise<MaderaReportLink> => {
    const {companyId, farmUnit, reportType, parcel, year, farmName} = options;

    const params: Record<string, string> = {companyId};

    if (reportType !== undefined) {
        params.reportType = reportType;
        if (reportType === ReportType.FARM_UNIT && farmUnit) {
            params.farmUnit = farmUnit;
        }
    }

    if (parcel) {
        params.parcel = parcel;
    }

    if (year) {
        params.year = String(year);
    }

    if (farmName) {
        params.farmName = farmName;
    }

    return apiClient
        .get<MaderaReportLink>(`/madera-reports/download`, {params})
        .then((response) => response.data)
        .catch(handleAxiosError);
};
